// src/features/services/ttlock/api/client.ts
/* Core HTTP + origin + auth header plumbing used by all TTLock API modules */

export interface GatewayErrorBody { [k: string]: any }

export class GatewayError extends Error {
  status: number
  body: GatewayErrorBody | string | undefined
  constructor(message: string, status: number, body?: GatewayErrorBody | string) {
    super(message)
    this.name = 'GatewayError'
    this.status = status
    this.body = body
  }
}

export const isOfflineError = (e: unknown) =>
  e instanceof GatewayError && e.status === 409

/* ── origin resolution (browser, vite, CRA, SSR) ───────────────────── */
function resolveOrigin(): string {
  const IS_BROWSER = typeof window !== 'undefined'
  const w: any = IS_BROWSER ? window : undefined
  // Safe access to process.env without requiring Node global typings
  const ENV: any = (typeof globalThis !== 'undefined' && (globalThis as any).process && (globalThis as any).process.env) || {}

  const fromWindow =
    (IS_BROWSER && (w?.__TTLOCK_API_ORIGIN__ ?? w?.TTLOCK_API_ORIGIN)) || undefined

  // Avoid direct import.meta access to keep CRA/webpack happy
  const fromVite = undefined

  const fromCRA = ENV.REACT_APP_TTLOCK_API_ORIGIN || undefined

  const fromNode = (!IS_BROWSER && (ENV.TTLOCK_API_ORIGIN || ENV.REACT_APP_TTLOCK_API_ORIGIN)) || undefined

  const devDefault =
    IS_BROWSER && window.location.port === '3000' ? 'http://localhost:8080' : undefined

  return fromWindow || fromVite || fromCRA || fromNode || devDefault ||
         (IS_BROWSER ? window.location.origin : 'http://localhost:8080')
}

let API_ORIGIN = resolveOrigin()
export function setApiOrigin(origin: string) { API_ORIGIN = origin }

/* Optional auth header hook (JWT etc) */
export type AuthHeaderProvider = () => Record<string, string> | undefined
let authHeaderProvider: AuthHeaderProvider | null = null
export function setAuthHeaderProvider(fn: AuthHeaderProvider) { authHeaderProvider = fn }

/* ── tiny fetch helper ─────────────────────────────────────────────── */
export async function http<T>(
  path: string,
  init?: RequestInit,
  qs?: Record<string, string | number>
): Promise<T> {
  const url = new URL(path, API_ORIGIN)
  if (qs) for (const [k, v] of Object.entries(qs)) url.searchParams.set(k, String(v))

  const headers: Record<string, string> = {
    ...(init?.headers as Record<string, string> | undefined),
  }
  const extraAuth = authHeaderProvider?.()
  if (extraAuth) Object.assign(headers, extraAuth)

  // Attach CSRF automatically if cookie present and not manually set
  try {
    if (!headers['X-CSRF']) {
      const csrfMatch = (typeof document !== 'undefined' ? document.cookie : '')
        .split(';')
        .map(s => s.trim())
        .find(s => s.startsWith('csrf='));
      if (csrfMatch) {
        const val = csrfMatch.split('=')[1];
        if (val) headers['X-CSRF'] = decodeURIComponent(val);
      }
    }
  } catch { /* ignore */ }

  const res = await fetch(url.toString(), { ...init, headers, credentials: 'include' })
  const text = await res.text().catch(() => '')

  const tryJson = () => {
    try { return text ? JSON.parse(text) : undefined } catch { return undefined }
  }

  if (!res.ok) {
    const body = tryJson() ?? text
    throw new GatewayError(`${init?.method || 'GET'} ${url.pathname} → ${res.status}`, res.status, body)
  }

  return (tryJson() ?? ({} as any)) as T
}
